import { Injectable, NgZone } from '@angular/core';
import { Globals } from 'app/_models/globals';
import { SweetAlertParams, ToastParams } from 'app/_models/notificationParams.model';
import Swal, { SweetAlertIcon, SweetAlertInput, SweetAlertPosition, SweetAlertResult } from 'sweetalert2';

declare const toastr: any;

@Injectable()
export class NotificationService {
    constructor(private globals: Globals, private ngZone: NgZone) {
        window['angularComponentReference'] = { component: this, zone: this.ngZone, showWarning: (message, title) => this.showWarning(message, title), };
    }

    async buildSweetAlertParams(params: SweetAlertParams): Promise<SweetAlertParams> {
        const allowEscapeKey = params.allowEscapeKey != null ? params.allowEscapeKey : true;
        const allowOutsideClick = params.allowOutsideClick != null ? params.allowOutsideClick : true;
        const bigSwall: boolean = params.bigSwall || false;
        const cancelButtonText: string = params.cancelButtonText ? this.globals.translate(params.cancelButtonText) : this.globals.translate('elemento.generico-botao.cancelar');
        const confirmButtonColor: string = params.confirmButtonColor || '';
        const confirmButtonText: string = params.confirmButtonText ? this.globals.translate(params.confirmButtonText) : this.globals.translate('elemento.generico-botao.ok');
        const customClass: any = params.customClass || {};
        const denyButtonText: string = params.denyButtonText ? this.globals.translate(params.denyButtonText) : undefined;
        const didOpen: (value?: any) => any = params.didOpen || (() => { });
        const footer: string = params.footer || '';
        const icon: SweetAlertIcon = params.icon as SweetAlertIcon;
        const iconColor: string = params.iconColor || '';
        const input: SweetAlertInput = params.input || undefined;
        const inputOptions: any = params.inputOptions || undefined;
        const inputPlaceholder: string = params.inputPlaceholder ? this.globals.translate(params.inputPlaceholder) : '';
        const inputValue: any = params.inputValue || '';
        const inputValidator: (value?: any) => any = params.inputValidator || (() => { });
        const isHtml: boolean = params.isHtml || false;
        const message: string = isHtml ? params.message : (params.message ? (params.notTranslateMessage ? params.message : this.globals.translate(params.message)) : '');
        const position: SweetAlertPosition = params.position || 'center';
        const preConfirm: (value?: any) => any = params.preConfirm != null ? params.preConfirm : (() => { });
        const returnInputValueOnDeny: boolean = params.returnInputValueOnDeny || false;
        const showDenyButton: boolean = params.showDenyButton || false;
        const showCancelButton: boolean = params.showCancelButton != null ? params.showCancelButton : true ;
        const showCloseButton: boolean = params.showCloseButton != null ? params.showCloseButton : true;
        const showConfirmButton: boolean = params.showConfirmButton != null ? params.showConfirmButton : true;
        const title: string = params.title ? (params.notTranslateTitle ?  params.title : this.globals.translate(params.title)) : this.globals.translate('notification.confirm.title');

        return {
            allowEscapeKey, allowOutsideClick,
            bigSwall, didOpen,
            cancelButtonText, confirmButtonColor, showCancelButton, showCloseButton, showConfirmButton, confirmButtonText, showDenyButton, denyButtonText,
            customClass, icon, iconColor,
            input, inputOptions, inputPlaceholder, inputValue, inputValidator, preConfirm, returnInputValueOnDeny,
            footer, isHtml, message, position, title
        };
    }

    clearToastr() {
        toastr.clear();
    }

    private setOptionsToastr(override?: Partial<any>): Partial<any> {
        override = override || {};
        override.closeButton = true;

        return override;
    }

    async showConfirm(params: SweetAlertParams): Promise<SweetAlertResult> {
        params = await this.buildSweetAlertParams(params);

        return Swal.fire({
            allowEscapeKey: params.allowEscapeKey,
            allowOutsideClick: params.allowOutsideClick,
            didOpen: params.didOpen,
            width: params.bigSwall ? 700 : undefined,
            html: params.isHtml ? params.message : undefined,
            title: params.title,
            text: !params.isHtml ? params.message : undefined,
            icon: params.icon,
            input: params.input,
            inputOptions: params.inputOptions,
            inputPlaceholder: params.inputPlaceholder,
            inputValue: params.inputValue,
            inputValidator: params.inputValidator,
            preConfirm: params.preConfirm,
            showCancelButton: params.showCancelButton,
            showCloseButton: params.showCloseButton,
            showConfirmButton: params.showConfirmButton,
            showDenyButton: params.showDenyButton,
            denyButtonText: params.denyButtonText,
            confirmButtonText: params.confirmButtonText,
            cancelButtonText: params.cancelButtonText,
            customClass: params.customClass,
            footer: params.footer,
            returnInputValueOnDeny: params.returnInputValueOnDeny
        }).then((result) => { return result });
    }

    async showConfirmationDialog(title: string, message?: string, translate: boolean = true): Promise<boolean> {
        let params = await this.buildSweetAlertParams({ title: title, message: message, notTranslateTitle: !translate });

        return Swal.fire({
            title: params.title,
            text: params.message,
            showCancelButton: true,
            showCloseButton: false,
            confirmButtonText: params.confirmButtonText,
            cancelButtonText: params.cancelButtonText,
        }).then((result) => { return result.isConfirmed });
    }

    async showInfo(params: SweetAlertParams): Promise<SweetAlertResult> {
        params = await this.buildSweetAlertParams(params);

        return Swal.fire({
            width: params.bigSwall ? 700 : undefined,
            html: params.isHtml ? params.message : undefined,
            title: params.title,
            text: !params.isHtml ? params.message : undefined,
            icon: params.icon,
            input: params.input,
            inputOptions: params.inputOptions,
            inputPlaceholder: params.inputPlaceholder,
            inputValue: params.inputValue,
            inputValidator: params.inputValidator,
            showCancelButton: false,
            showCloseButton: true,
            showDenyButton: params.showDenyButton,
            denyButtonText: params.denyButtonText,
            confirmButtonText: params.confirmButtonText,
            cancelButtonText: params.cancelButtonText,
            customClass: params.customClass,
            footer: params.footer,
            returnInputValueOnDeny: params.returnInputValueOnDeny
        }).then((result) => { return result });
    }

    async showWarning(message?: string, title?: string, translateMessage: boolean = true, translateTitle: boolean = true, messageIsHtml: boolean = false){
        let params = await this.buildSweetAlertParams({ message, notTranslateMessage: !translateMessage, notTranslateTitle: !translateTitle, title, isHtml: messageIsHtml });

        return Swal.fire({
            confirmButtonText: params.confirmButtonText,
            icon: 'warning',
            showCancelButton: false,
            showCloseButton: true,
            title: params.title,
            html: params.isHtml ? params.message : undefined,
            text: !params.isHtml ? params.message : undefined,
        }).then((result) => { return result.isConfirmed });
    }

    public async showWarningWithVariables(messageKey: string, replacements: { [key: string]: string }) {
        let message = this.globals.translate(messageKey);

        for (const key in replacements) {
            message = message.replace(`{{${key}}}`, replacements[key]);
        }

        await this.showWarning(message, null, false);
    };

    async showInfoWithoutButtons(params: SweetAlertParams): Promise<SweetAlertResult> {
        params = await this.buildSweetAlertParams(params);

        return Swal.fire({
            width: params.bigSwall ? 700 : undefined,
            html: params.isHtml ? params.message : undefined,
            title: params.title,
            text: !params.isHtml ? params.message : undefined,
            icon: params.icon,
            input: params.input,
            inputOptions: params.inputOptions,
            inputPlaceholder: params.inputPlaceholder,
            inputValue: params.inputValue,
            inputValidator: params.inputValidator,
            showCancelButton: false,
            showConfirmButton: false,
            showCloseButton: true,
            showDenyButton: params.showDenyButton,
            denyButtonText: params.denyButtonText,
            confirmButtonText: params.confirmButtonText,
            cancelButtonText: params.cancelButtonText,
            customClass: params.customClass,
            footer: params.footer,
            returnInputValueOnDeny: params.returnInputValueOnDeny
        }).then((result) => { return result });
    }

    showErrorToastr(message?: string, params?: ToastParams) {
        const override = this.setOptionsToastr(params?.override);

        if (params?.translateMessage)
            message = this.globals.translate(message, params?.messageParameters);

        toastr.error(message, this.globals.translate("elemento.generico-toastr.erro"), override);
    }

    showSuccessToastr(message?: string, params?: ToastParams) {
        const override = this.setOptionsToastr(params?.override);

        if (params?.translateMessage)
            message = this.globals.translate(message, params?.messageParameters);

        toastr.success(message, this.globals.translate("elemento.generico-toastr.sucesso"), override);
    }

    showWarningToastr(message?: string, params?: ToastParams) {
        const override = this.setOptionsToastr(params?.override);

        if (params?.translateMessage)
            message = this.globals.translate(message, params?.messageParameters);

        toastr.warning(message, this.globals.translate("elemento.generico-toastr.alerta"), override);
    }
}
